'use client';

import { HTMLMotionProps, LayoutGroup, motion, MotionValue, useTransform } from 'motion/react';
import { useRef } from 'react';
import { TickerItemContext } from './TickerItemContext';

export type ItemSize = 'auto' | 'fill';

/**
 * Represents an individual item within the Ticker.
 *
 * This component handles the logic for repositioning items to create the infinite scroll effect.
 *
 * @param offset - The current scroll offset of the ticker.
 * @param axis - The scroll axis ("x" or "y").
 * @param listSize - The total size of the list including all clones.
 * @param itemIndex - The original index of the item.
 * @param cloneIndex - The index of the clone, if this is a cloned item.
 * @param props - HTML attributes to pass to the list item li element.
 * @returns A ListItem React component.
 */
export function TickerItem({
  offset,
  axis,
  listSize = 0,
  itemIndex,
  cloneIndex,
  bounds,
  inset,
  alignItems,
  itemSize = 'auto',
  ...props
}: HTMLMotionProps<'li'> & {
  offset: MotionValue<number>;
  axis: 'x' | 'y';
  listSize?: number;
  inset: number;
  bounds: { start: number; end: number };
  itemIndex?: number;
  cloneIndex?: number;
  alignItems: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  itemSize?: ItemSize;
}) {
  const ref = useRef<HTMLLIElement>(null);
  const { start, end } = bounds;

  const transform = useTransform(() => {
    const currentOffset = offset.get();
    if ((!start && !end) || !listSize) return 0;

    if (currentOffset + end <= -inset) {
      return listSize;
    }

    return 0;
  });

  const itemOffset = useTransform(() => {
    const currentOffset = offset.get();
    const currentTransform = transform.get();
    if ((!start && !end) || !listSize) return 0;

    return currentOffset + start + currentTransform;
  });

  const itemWrapperOffAxisSize = alignItems === 'stretch' ? '100%' : 'fit-content';

  const isClone = cloneIndex !== undefined;

  return (
    <TickerItemContext.Provider value={{ offset: itemOffset }}>
      <LayoutGroup inherit="id" id={isClone ? `clone-${cloneIndex}` : undefined}>
        <motion.li
          {...props}
          ref={ref}
          className={cloneIndex === undefined ? 'ticker-item' : 'clone-item'}
          style={{
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: itemSize === 'fill' ? '100%' : undefined,
            display: itemSize === 'fill' ? 'flex' : undefined,
            height: axis === 'x' ? itemWrapperOffAxisSize : undefined,
            width: axis === 'y' ? itemWrapperOffAxisSize : undefined,
            x: axis === 'x' ? transform : 0,
            y: axis === 'y' ? transform : 0,
          }}
          aria-hidden={isClone ? true : undefined}
        />
      </LayoutGroup>
    </TickerItemContext.Provider>
  );
}
